import React, { useState, useMemo } from "react";
import {
  Title,
  GoBack,
  Message,
  TextArea,
  InputField,
  ActionButton,
  MyButton,
} from "../../component/Component";
import { FOLDERS } from "../../../doseeData/utils/Utils";
import { codeOk, errorMessage } from "../../../../middlewares/index";
import { useBlogContext } from "../../../../context/blog/BlogContext";
import { defaultImage } from "../../../../middlewares/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import GenerateDescription from "../../../doseeData/utils/GenerateDescription";
import {
  FiFileText,
  FiUser,
  FiHash,
  FiInfo,
  FiUpload,
} from "react-icons/fi";
import { FaHandSparkles } from "react-icons/fa";

const Add = () => {
  const blogRoutes = "/blog";
  const { createOneBlog } = useBlogContext();

  const [status, setStatus] = useState(0);
  const [title, setTitle] = useState(null);
  const [error, setError] = useState(false);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [description, setDescription] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(null);

  const slugPreview = useMemo(
    () => (title ? String(title).toLowerCase().replace(/\s/g, "_") : "—"),
    [title]
  );

  const descMeta = useMemo(() => {
    const text = description || "";
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const chars = text.length;
    return { words, chars };
  }, [description]);

  const cleanFields = () => {
    setTitle(null);
    setAuthor(null);
    setSuccess(true);
    setLoading(false);
    setThumbnail(null);
    setThumbnailId(null);
    setDescription(null);
  };

  const responses = (isError, st, msg) => {
    setError(isError);
    setStatus(st);
    setErrorMessage(msg);
  };

  const addOneBlogHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, 400, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      title,
      author,
      description,
      reading_time: 10,
      slug: title?.toLowerCase().replace(/\s/g, "_"),
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "...",
    };

    const response = await createOneBlog(data);
    if (response) {
      if (response?.data?.status === codeOk || response?.data?.status === 201) {
        cleanFields();
        return responses(true, response?.data?.status, response?.data?.message);
      } else {
        setLoading(false);
        return responses(true, response?.data?.status, response?.data?.message);
      }
    } else {
      setLoading(false);
      return responses(true, 400, errorMessage);
    }
  };

  return (
    <div className="h-full overflow-y-auto rounded-b-xl bg-gradient-to-b from-white to-gray-50 p-4">
      <GoBack props={{ route: blogRoutes }} />

      {showDescriptionModal ? (
        <GenerateDescription
          setDescription={setDescription}
          setShowDescriptionModal={setShowDescriptionModal}
        />
      ) : null}

      <form onSubmit={addOneBlogHandler} className="mx-auto mt-2 max-w-6xl">
        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-2 px-5 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-gray-900 text-white">
                <FiFileText className="h-5 w-5" />
              </span>
              <div>
                <Title props={{ title: "Create Blog Post" }} />
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700">
                    <FiInfo className="h-3.5 w-3.5" />
                    Fill all fields then publish
                  </span>
                </div>
              </div>
            </div>
            <div className="text-[11px] text-gray-600">
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-[2px] font-semibold">
                <FiUpload className="h-3.5 w-3.5" />
                JPG/PNG recommended
              </span>
            </div>
          </div>

          <div className="px-5 pb-5">
            <div className="mt-4">
              <Message
                props={{
                  error,
                  success,
                  message,
                  loading,
                  status,
                  onClick: () => {
                    setError(false);
                    setSuccess(false);
                    setErrorMessage("");
                  },
                }}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <div className="rounded-2xl border border-gray-200 bg-white/90 p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">Cover Image</div>
                    <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100 px-2 py-[2px] text-[10px] font-semibold text-gray-700">
                      <FiUpload className="h-3 w-3" />
                      1600×900
                    </span>
                  </div>
                  <ImageKitUpload
                    thumbnail={thumbnail}
                    thumbnailId={thumbnailId}
                    setThumbnail={setThumbnail}
                    setThumbnailId={setThumbnailId}
                    folder={FOLDERS.BLOGS}
                  />
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="rounded-2xl border border-gray-200 bg-white/90 p-4 shadow-sm space-y-5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <label className="flex items-center justify-between text-[13px] font-semibold text-gray-800">
                        <span className="inline-flex items-center gap-2">
                          <FiFileText className="h-4 w-4 text-gray-600" />
                          Blog Title
                        </span>
                        <span className="rounded-md bg-gray-100 px-2 py-[2px] text-[10px] font-semibold text-gray-700">
                          Required
                        </span>
                      </label>
                      <InputField
                        props={{
                          id: "title",
                          title: "Blog Title",
                          type: "text",
                          value: title ? title : "",
                          onChange: (e) => {
                            setTitle(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <div className="mt-1 inline-flex items-center gap-2 text-[11px] text-gray-600">
                        <FiHash className="h-3.5 w-3.5" />
                        Slug: <span className="font-mono">{slugPreview}</span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-[13px] font-semibold text-gray-800">
                        <FiUser className="h-4 w-4 text-gray-600" />
                        Author
                      </label>
                      <InputField
                        props={{
                          id: "author",
                          title: "Author",
                          type: "text",
                          value: author ? author : "",
                          onChange: (e) => {
                            setAuthor(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Title props={{ title: "Content" }} />
                      <div className="inline-flex items-center gap-2 text-[11px] text-gray-600">
                        <span className="rounded-md border border-gray-200 min-w-fit bg-gray-100 px-2 py-[2px] font-semibold">
                          {descMeta.words} words · {descMeta.chars} chars
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <MyButton
                        onClick={() => setShowDescriptionModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-blue-700 to-blue-600 px-3 py-2 text-xs font-semibold text-white shadow hover:from-blue-800 hover:to-blue-700"
                      >
                        <FaHandSparkles className="h-4 w-4" />
                        Generate Description
                      </MyButton>
                    </div>

                    <TextArea
                      props={{
                        id: "description",
                        title: "Description",
                        type: "text",
                        value: description ? description : "",
                        onChange: (e) => {
                          setDescription(e.target.value);
                          setError(null);
                          setSuccess(null);
                        },
                      }}
                    />

                    <div className="flex items-center gap-2 text-[11px] text-gray-600">
                      <FiInfo className="h-3.5 w-3.5" />
                      Aim for clear, concise language. You can refine after saving.
                    </div>
                  </div>

                  <div className="pt-2">
                    <ActionButton
                      props={{
                        title: "Add Blog",
                        error,
                        success,
                        message,
                        loading,
                        status,
                        onClick: () => {
                          setError(false);
                          setSuccess(false);
                          setErrorMessage("");
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Add;
